import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import {
  CmpProspectFieldsFragment,
  GetCmpManageProspectsQuery,
  useExportCmpProspectsCsvLazyQuery,
  useGetCmpManageProspectsQuery,
} from "../../../../graphql/types";
import { RoutePaths } from "../../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { commonStyles } from "../../../common/commonStyles";
import {
  DataTable,
  DataTableColumn,
} from "../../../common/dashboard/DataTable";
import { EmptyState } from "../../../common/dashboard/EmptyState";
import {
  FilterBar,
  FilterSearchField,
} from "../../../common/dashboard/FilterBar";
import { cmpLongDateFormatter } from "../../../common/dashboard/formatters";
import { LoadingOverlay } from "../../../common/dashboard/LoadingOverlay";
import {
  ManagePageSection,
  ManagePageShell,
  ManagePageStatusMessage,
} from "../../../common/dashboard/ManagePageShell";
import { downloadTextFile } from "../../Invoicing/invoiceFileDownloads";

type SortDirection = "asc" | "desc";

type SortableColumnKey =
  | "companyName"
  | "mainContactName"
  | "mainContactEmail"
  | "leadCaptureLabel"
  | "createdByName"
  | "createdAtUtcMilli";

type ManageProspectsColumnKey = SortableColumnKey | "action";

type ProspectRow =
  GetCmpManageProspectsQuery["getCmpManageProspects"]["prospects"][number];

type AnalyticsMetric = {
  currentValue: number;
  label: string;
  previousValue: number;
};

type ManageProspectsLocationState = {
  createdProspect?: CmpProspectFieldsFragment;
  statusMessage?: string;
};

const integerFormatter = new Intl.NumberFormat("en-CA", {
  maximumFractionDigits: 0,
});

const pageHeaderActionButtonSx = {
  alignSelf: { xs: "flex-start", sm: "center" },
  backgroundColor: commonStyles.cpaGold,
  borderRadius: "6px",
  color: "white",
  fontSize: 16,
  fontWeight: 800,
  minHeight: 52,
  px: 3,
  textTransform: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    mr: 1,
    "& svg": { fontSize: 22 },
  },
  "&:hover": { backgroundColor: commonStyles.cpaGoldDark },
};

const tableActionButtonSx = {
  backgroundColor: commonStyles.cpaGold,
  borderRadius: "4px",
  boxShadow: "none",
  color: "white",
  fontSize: 11,
  fontWeight: 800,
  justifyContent: "center",
  lineHeight: 1,
  minHeight: 30,
  minWidth: 176,
  overflowWrap: "normal",
  px: 1.25,
  py: 0.75,
  textTransform: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: 0.75,
    "& svg": { fontSize: 14 },
  },
  "&:hover": {
    backgroundColor: commonStyles.cpaGoldDark,
    boxShadow: "none",
  },
};

const normalizeSearchText = (value?: string | null) =>
  (value || "").trim().toLocaleLowerCase();

const getProspectSearchText = (prospect: ProspectRow) =>
  [
    prospect.companyName,
    prospect.companyWebsite,
    prospect.mainContactName,
    prospect.mainContactEmail,
    prospect.mainPhoneNumber,
    prospect.mainPhoneNumberExtension,
    prospect.leadCaptureLabel,
    prospect.createdByName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();

const getProspectSortValue = (
  prospect: ProspectRow,
  column: SortableColumnKey,
): string | number => {
  if (column === "createdAtUtcMilli") {
    return prospect.createdAtUtcMilli;
  }

  return normalizeSearchText(prospect[column]);
};

const sortProspects = ({
  column,
  direction,
  prospects,
}: {
  column: SortableColumnKey;
  direction: SortDirection;
  prospects: ReadonlyArray<ProspectRow>;
}) =>
  [...prospects].sort((firstProspect, secondProspect) => {
    const firstValue = getProspectSortValue(firstProspect, column);
    const secondValue = getProspectSortValue(secondProspect, column);
    let result = 0;

    if (typeof firstValue === "number" && typeof secondValue === "number") {
      result = firstValue - secondValue;
    } else {
      result = String(firstValue).localeCompare(String(secondValue), "en-CA", {
        numeric: true,
        sensitivity: "base",
      });
    }

    if (result === 0) {
      result = firstProspect.companyName.localeCompare(
        secondProspect.companyName,
        "en-CA",
        { sensitivity: "base" },
      );
    }

    return direction === "desc" ? -result : result;
  });

const getAnalyticsTrend = ({
  currentValue,
  previousValue,
}: Pick<AnalyticsMetric, "currentValue" | "previousValue">) => {
  if (currentValue === previousValue) {
    return {
      className: "text-gray-600",
      text: "No change from last year",
    };
  }

  if (previousValue === 0) {
    return {
      className: "text-green-500",
      text: "New this year",
    };
  }

  const increased = currentValue >= previousValue;
  const percentageChange = Math.round(
    (Math.abs(currentValue - previousValue) / Math.abs(previousValue)) * 100,
  );

  return {
    className: increased ? "text-green-500" : "text-red-500",
    text: `${percentageChange}% ${
      increased ? "Increase" : "decrease"
    } from last year`,
  };
};

const AnalyticsCard = ({
  currentValue,
  label,
  previousValue,
}: AnalyticsMetric) => {
  const trend = getAnalyticsTrend({ currentValue, previousValue });

  return (
    <article className="min-h-[132px] rounded-lg border border-gray-300 bg-white p-5">
      <h3 className="text-[14px] font-bold leading-5 text-black">{label}</h3>
      <p className="mt-3 text-2xl font-bold text-gray-950">
        {integerFormatter.format(Math.round(currentValue))}
      </p>
      <p className={`mt-3 text-sm font-bold ${trend.className}`}>
        {trend.text}
      </p>
    </article>
  );
};

const formatProspectDate = (utcMilli: number) =>
  cmpLongDateFormatter.format(new Date(utcMilli));

const ManageProspects = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] =
    useState<SortableColumnKey>("companyName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const locationState = location.state as ManageProspectsLocationState | null;
  const [optimisticCreatedProspect, setOptimisticCreatedProspect] =
    useState<CmpProspectFieldsFragment | null>(
      () => locationState?.createdProspect || null,
    );
  const [statusMessage, setStatusMessage] =
    useState<ManagePageStatusMessage | null>(() =>
      locationState?.statusMessage
        ? { severity: "success", message: locationState.statusMessage }
        : null,
    );

  useEffect(() => {
    if (!locationState?.createdProspect) {
      return;
    }

    // Keep the just-created record in local component state, but retire it
    // from browser history immediately. Otherwise revisiting this history
    // entry after conversion could resurrect a stale ACTIVE prospect.
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: locationState.statusMessage
        ? { statusMessage: locationState.statusMessage }
        : null,
    });
  }, [
    location.hash,
    location.pathname,
    location.search,
    locationState?.createdProspect,
    locationState?.statusMessage,
    navigate,
  ]);

  const manageProspectsVariables = useMemo(
    () => ({ input: { chamberId } }),
    [chamberId],
  );

  const {
    data: manageProspectsData,
    loading: manageProspectsLoading,
    error: manageProspectsError,
    refetch: refetchManageProspects,
  } = useGetCmpManageProspectsQuery({
    variables: manageProspectsVariables,
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });

  const [exportCmpProspectsCsv, { loading: exportLoading }] =
    useExportCmpProspectsCsvLazyQuery({ fetchPolicy: "network-only" });

  const serverProspects = useMemo(
    () => manageProspectsData?.getCmpManageProspects.prospects || [],
    [manageProspectsData],
  );

  useEffect(() => {
    if (
      optimisticCreatedProspect &&
      serverProspects.some(
        (prospect) => prospect.id === optimisticCreatedProspect.id,
      )
    ) {
      setOptimisticCreatedProspect(null);
    }
  }, [optimisticCreatedProspect, serverProspects]);

  useEffect(() => {
    const optimisticProspectId = optimisticCreatedProspect?.id;
    if (!optimisticProspectId || !chamberId) {
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let timeoutId: number | undefined;
    const scheduleRefetch = (delay: number) => {
      timeoutId = window.setTimeout(() => {
        void refetchUntilAcknowledged();
      }, delay);
    };
    const refetchUntilAcknowledged = async () => {
      if (cancelled) {
        return;
      }

      try {
        const result = await refetchManageProspects(manageProspectsVariables);
        if (
          result.data?.getCmpManageProspects.prospects.some(
            (prospect) => prospect.id === optimisticProspectId,
          )
        ) {
          setOptimisticCreatedProspect((currentProspect) =>
            currentProspect?.id === optimisticProspectId
              ? null
              : currentProspect,
          );
          return;
        }
      } catch (_) {
        // The normal query error state remains visible while bounded retries
        // cover DynamoDB index propagation.
      }

      attempt += 1;
      if (!cancelled && attempt < 6) {
        scheduleRefetch(500 * attempt);
      }
    };

    scheduleRefetch(500);
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    chamberId,
    manageProspectsVariables,
    optimisticCreatedProspect?.id,
    refetchManageProspects,
  ]);
  const allProspects = useMemo(
    () =>
      optimisticCreatedProspect
        ? [optimisticCreatedProspect, ...serverProspects]
        : serverProspects,
    [optimisticCreatedProspect, serverProspects],
  );

  const prospects = useMemo(() => {
    const normalizedSearchQuery = normalizeSearchText(searchQuery);
    const filteredProspects = normalizedSearchQuery
      ? allProspects.filter((prospect) =>
          getProspectSearchText(prospect).includes(normalizedSearchQuery),
        )
      : allProspects;

    return sortProspects({
      column: sortColumn,
      direction: sortDirection,
      prospects: filteredProspects,
    });
  }, [allProspects, searchQuery, sortColumn, sortDirection]);

  const analytics = manageProspectsData?.getCmpManageProspects.analytics;
  const analyticsMetrics: AnalyticsMetric[] = analytics
    ? [
        {
          currentValue:
            analytics.totalProspectsYtd + (optimisticCreatedProspect ? 1 : 0),
          label: "Total Number of Prospect YTD",
          previousValue: analytics.previousFiscalYearTotalProspectsYtd,
        },
        {
          currentValue:
            analytics.newProspectsLast30Days +
            (optimisticCreatedProspect ? 1 : 0),
          label: "New Prospect (Last 30 days)",
          previousValue: analytics.previousYearNewProspectsSame30DayPeriod,
        },
        {
          currentValue: analytics.convertedToMembersYtd,
          label: "Converted to Members YTD",
          previousValue: analytics.previousFiscalYearConvertedToMembersYtd,
        },
      ]
    : [];

  const handleRequestSort = (column: SortableColumnKey) => {
    const isSameColumn = sortColumn === column;
    setSortColumn(column);
    setSortDirection(isSameColumn && sortDirection === "asc" ? "desc" : "asc");
  };

  const handleExport = useCallback(async () => {
    if (!chamberId) {
      setStatusMessage({
        severity: "error",
        message: "Chamber context is still loading. Try again in a moment.",
      });
      return;
    }
    if (optimisticCreatedProspect) {
      setStatusMessage({
        severity: "info",
        message:
          "The new prospect is still syncing. The export will be available once it appears in the server list.",
      });
      return;
    }

    try {
      const normalizedSearchQuery = searchQuery.trim();
      const result = await exportCmpProspectsCsv({
        variables: {
          input: {
            chamberId,
            search: normalizedSearchQuery || undefined,
          },
        },
      });
      const exportResult = result.data?.exportCmpProspectsCsv;

      if (!exportResult) {
        throw new Error("No prospect export response was returned.");
      }

      downloadTextFile({
        filename: exportResult.filename,
        contentType: exportResult.contentType,
        content: exportResult.csvContent,
      });

      setStatusMessage({
        severity: "success",
        message: `${exportResult.filename} prepared with ${integerFormatter.format(
          exportResult.rowCount,
        )} ${exportResult.rowCount === 1 ? "prospect" : "prospects"} on ${formatProspectDate(
          exportResult.generatedAtUtcMilli,
        )}.`,
      });
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not prepare the prospect list export.",
      });
    }
  }, [
    chamberId,
    exportCmpProspectsCsv,
    optimisticCreatedProspect,
    searchQuery,
  ]);

  const pageHeaderActions = useMemo(
    () => (
      <Button
        type="button"
        variant="contained"
        startIcon={<FileUploadOutlinedIcon />}
        onClick={() => void handleExport()}
        disabled={
          exportLoading || !chamberId || !!optimisticCreatedProspect
        }
        sx={pageHeaderActionButtonSx}
      >
        {exportLoading
          ? "Preparing Export..."
          : optimisticCreatedProspect
            ? "Syncing Prospect..."
            : "Export Prospect List"}
      </Button>
    ),
    [
      chamberId,
      exportLoading,
      handleExport,
      optimisticCreatedProspect,
    ],
  );

  const headerOverride = useMemo(
    () => ({
      title: "Manage Prospects",
      description:
        "Track and manage prospective members. View prospect details, and convert prospects to members.",
      actions: pageHeaderActions,
      withHeaderDivider: true,
    }),
    [pageHeaderActions],
  );

  const columns = useMemo<
    DataTableColumn<ProspectRow, ManageProspectsColumnKey>[]
  >(
    () => [
      {
        key: "companyName",
        header: "Prospect Company",
        sortable: true,
        width: "13%",
        render: (prospect) => prospect.companyName,
      },
      {
        key: "mainContactName",
        header: "Contact",
        sortable: true,
        width: "16%",
        render: (prospect) => prospect.mainContactName || "-",
      },
      {
        key: "mainContactEmail",
        header: "Email",
        sortable: true,
        width: "17%",
        render: (prospect) => prospect.mainContactEmail || "-",
      },
      {
        key: "leadCaptureLabel",
        header: "Lead Capture",
        sortable: true,
        width: "13%",
        render: (prospect) => (
          <span className="font-bold text-gray-950">
            {prospect.leadCaptureLabel}
          </span>
        ),
      },
      {
        key: "createdByName",
        header: "Created by",
        sortable: true,
        width: "11%",
        render: (prospect) =>
          prospect.createdByName ? (
            <span className="font-bold text-gray-950">
              {prospect.createdByName}
            </span>
          ) : (
            "-"
          ),
      },
      {
        key: "createdAtUtcMilli",
        header: "Created Date",
        sortable: true,
        width: "13%",
        render: (prospect) => formatProspectDate(prospect.createdAtUtcMilli),
      },
      {
        key: "action",
        header: "Action",
        headerCellSx: { fontWeight: 800, width: "17%" },
        stickyRight: true,
        render: (prospect) => (
          <Button
            type="button"
            size="small"
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            aria-label={`Update ${prospect.companyName} prospect member`}
            onClick={() =>
              navigate(
                generatePath(RoutePaths.chamberAdmin_prospects_update, {
                  prospectId: prospect.id,
                }),
              )
            }
            sx={tableActionButtonSx}
          >
            Update Prospect Member
          </Button>
        ),
      },
    ],
    [navigate],
  );

  const analyticsLoading =
    (!chamberId || manageProspectsLoading) && analyticsMetrics.length === 0;

  return (
    <ManagePageShell
      headerOverride={headerOverride}
      statusMessage={statusMessage}
      onStatusMessageClose={() => setStatusMessage(null)}
    >
      <ManagePageSection
        className="-mx-8 px-8 pt-6"
        title="Prospect Analytics to (Fiscal Year to Date)"
        description="See a quick overview of your prospect pipeline and engagement"
        descriptionClassName="mt-1 text-sm text-black"
      >
        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-6">
          {analyticsMetrics.length > 0 ? (
            analyticsMetrics.map((metric) => (
              <AnalyticsCard key={metric.label} {...metric} />
            ))
          ) : analyticsLoading ? (
            <div className="col-span-full flex items-center gap-3 rounded-lg border border-gray-200 p-5 text-sm font-semibold text-gray-500">
              <LoadingOverlay label="Loading fiscal year analytics..." />
            </div>
          ) : (
            <div className="col-span-full rounded-lg border border-gray-200 p-5 text-sm font-semibold text-gray-500">
              Prospect analytics are unavailable.
            </div>
          )}
        </div>
      </ManagePageSection>

      <ManagePageSection
        className="mt-10 -mx-8 px-8"
        title="Prospect Filters"
        description="Search prospect name or contact details (e.g.: Prospect Name, Email, Phone Number)"
      >
        <FilterBar className="mt-5 w-full">
          <FilterSearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Enter prospect name or prospect details"
            className="w-full"
          />
        </FilterBar>
      </ManagePageSection>

      {!chamberId && (
        <Alert severity="info" className="mt-5">
          Chamber context is loading before prospect data can be shown.
        </Alert>
      )}

      {manageProspectsError && (
        <Alert severity="error" className="mt-5">
          {manageProspectsError.message ||
            "Could not load prospects from the backend."}
        </Alert>
      )}

      <ManagePageSection className="mt-10" title="Prospect List">
        <DataTable<ProspectRow, ManageProspectsColumnKey>
          ariaLabel="manage prospects table"
          columns={columns}
          rows={prospects}
          getRowKey={(prospect) => prospect.id}
          sort={{
            column: sortColumn,
            direction: sortDirection,
            onRequestSort: (column) => {
              if (column !== "action") {
                handleRequestSort(column);
              }
            },
          }}
          showLoadingRow={
            !chamberId || (manageProspectsLoading && allProspects.length === 0)
          }
          loadingRowContent={<LoadingOverlay label="Loading prospects..." />}
          showEmptyRow={
            Boolean(chamberId) &&
            !manageProspectsLoading &&
            !manageProspectsError &&
            prospects.length === 0
          }
          emptyRowContent={
            <EmptyState message="No prospects match the current search." />
          }
        />
      </ManagePageSection>
    </ManagePageShell>
  );
};

export default ManageProspects;
